/**
 * Abonelikler — musteri neye sahip, ne kadar kredisi var, ne zaman bitiyor.
 *
 * ── NEDEN KATEGORIYE GORE GRUPLU ─────────────────────────────────────────────
 * Musteri tek tek urunleri degil katalog aboneligini satin alir. Satin alma
 * karari katalog duzeyinde verildigi icin sayfa da o duzeyde konusmali;
 * urunler ilgili kategorinin altinda listelenir.
 *
 * Her katalog ayrı abonelik ve ayrı kredi cüzdanıdır. Bakiye kategori kartının
 * içinde gösterilir; sayfa hiçbir yerde kataloglar arası toplam üretmez.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { Badge, Card, EmptyState, LoadingBlock, PageHeader } from '@/components/ui';
import { canonicalCatalogName, catalogNameLanguage, formatDate, formatNumber, slugifyTr } from '@/lib/format';
import { categoryHue, categoryOrder } from './Home';
import type { CatalogItem } from '@shared/index';
import { copy, localizeCatalogName, useI18n } from '@/lib/i18n';
import './Subscriptions.css';

export default function Subscriptions() {
  const me = useMe();
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);

  const owned = useQuery({
    queryKey: ['catalog', { ownedOnly: true }],
    queryFn: () => api.get<{ items: CatalogItem[] }>('/catalog', { ownedOnly: 'true' }),
  });
  const offers = useQuery({
    queryKey: ['account', 'offers'],
    queryFn: () => api.get<{ items: Array<{ id: string; title: string; status: string; currency: string; totalMinor: number | null; currentRevision: number; updatedAt: string }> }>('/account/offers'),
  });
  const invoices = useQuery({
    queryKey: ['account', 'invoices'],
    queryFn: () => api.get<{ items: Array<{ id: string; number: string; status: string; currency: string; totalMinor: number; issuedAt: string | null; dueAt: string | null; documentUrl: string | null }> }>('/account/invoices'),
  });

  const items = owned.data?.items ?? [];

  /** Kategori -> urunler. Sira Kesfet ile ayni olsun diye `categoryOrder`. */
  const byCategory = new Map<string, CatalogItem[]>();
  for (const item of items) {
    const list = byCategory.get(item.category) ?? [];
    list.push(item);
    byCategory.set(item.category, list);
  }
  const categories = [...byCategory.keys()].sort(categoryOrder);

  return (
    <div className="subscriptions-page stack stack--lg flow-inset">
      <PageHeader
        title={c('Abonelikler', 'Subscriptions')}
        description={c('Sahip olduğun kataloglar, kalan kredin ve yenileme tarihlerin.', 'Your catalogs, remaining credits and renewal dates.')}
      />

      <section className="offers-section" aria-labelledby="offers-title">
        <div className="offers-section__head"><div><span>{c('Size özel', 'For your organization')}</span><h2 id="offers-title">{c('Tekliflerim', 'My proposals')}</h2></div><p>{c('Wondeon ekibinin kurumunuz için hazırladığı teklif ve revizyonları burada izleyin.', 'Track proposals and revisions prepared for your organization by the Wondeon team.')}</p></div>
        {(offers.data?.items.length ?? 0) === 0 ? <div className="offers-section__empty"><strong>{c('Paylaşılmış teklif yok', 'No shared proposals')}</strong><span>{c('Yeni bir teklif paylaşıldığında sidebar’da bildirim göreceksiniz.', 'You will see a sidebar notification when a new proposal is shared.')}</span></div> : <div className="offers-section__list">{offers.data!.items.map((offer) => <Link key={offer.id} to={`/abonelikler/teklifler/${offer.id}`}><span className="offers-section__mark">P</span><span><strong>{offer.title}</strong><small>{c('Teklif sürümü', 'Offer version')} {offer.currentRevision}</small></span><Badge tone={offer.status === 'accepted' || offer.status === 'payment_verified' ? 'success' : offer.status === 'rejected' ? 'danger' : 'primary'}>{offer.status === 'accepted' && offer.totalMinor === 0 ? c('Bedelsiz tamamlandı', 'Completed · free') : offerStatus(offer.status, language)}</Badge><b aria-hidden="true">→</b></Link>)}</div>}
      </section>

      <section className="offers-section" aria-labelledby="invoice-history-title">
        <div className="offers-section__head"><div><h2 id="invoice-history-title">{c('Fatura geçmişi', 'Invoice history')}</h2></div><p>{c('Kurumunuz adına kesilmiş faturaları ve güvenli belge bağlantılarını görüntüleyin.', 'View invoices issued to your organization and their secure document links.')}</p></div>
        {(invoices.data?.items.length ?? 0) === 0 ? <div className="offers-section__empty"><strong>{c('Fatura kaydı yok', 'No invoice records')}</strong><span>{c('Kesilmiş bir fatura olduğunda burada görünecek.', 'Issued invoices will appear here.')}</span></div> : <div className="offers-section__list">{invoices.data!.items.map((invoice) => <div className="subscription-invoice-row" key={invoice.id}><span className="offers-section__mark">F</span><span><strong>{invoice.number}</strong><small>{invoice.issuedAt ? formatDate(invoice.issuedAt) : c('Düzenleme bekliyor', 'Awaiting issue')} · {formatInvoiceMoney(invoice.totalMinor, invoice.currency, language)}</small></span><Badge tone={invoice.status === 'paid' ? 'success' : invoice.status === 'cancelled' ? 'danger' : 'primary'}>{invoice.status}</Badge>{invoice.documentUrl ? <a className="btn btn--secondary btn--sm" href={invoice.documentUrl} target="_blank" rel="noreferrer">{c('Belge', 'Document')}</a> : null}</div>)}</div>}
      </section>

      {/* ── Kataloglar ────────────────────────────────────────────────────── */}
      {owned.isLoading ? (
        <LoadingBlock rows={4} label={c('Abonelikler yükleniyor', 'Loading subscriptions')} />
      ) : categories.length === 0 ? (
        <EmptyState
          title={c('Henüz aboneliğin yok', 'You do not have a subscription yet')}
          description={c('Katalogları inceleyip ilgilendiğin paket için teklif alabilirsin.', 'Explore the catalogs and request a proposal for the package you are interested in.')}
          action={<Link className="btn btn--primary" to="/kesfet">{c("Keşfet'e git", 'Go to Discover')}</Link>}
        />
      ) : (
        <div className="subs-grid">
        {categories.map((category) => {
          const products = byCategory.get(category)!;
          const canonicalCategory = canonicalCatalogName(category);
          const displayCategory = localizeCatalogName(canonicalCategory, language);
          /* Bu kategorinin yetkileri: paket adi ve bitis tarihi oradan gelir. */
          const ents = me.entitlements.filter((e) =>
            products.some((p) => p.id === e.productId),
          );
          const trial = ents.some((e) => e.isTrial);
          /* En yakin bitis tarihi kategoriyi temsil eder — once o biter. */
          const expiries = products
            .map((p) => p.entitlementExpiresAt)
            .filter((v): v is string => Boolean(v))
            .sort();
          const packageNames = [
            ...new Set(ents.map((e) => e.viaPackageName).filter((v): v is string => Boolean(v))),
          ];
          const wallet = me.credits.find((credit) => credit.catalog === category);
          const spent = wallet ? Math.max(0, wallet.granted - wallet.balance) : 0;
          const usedPct = wallet && wallet.granted > 0 ? (spent / wallet.granted) * 100 : 0;

          return (
            <Card key={category} padded={false} className={`subs__cat subs__cat--${categoryHue(category)}`}>
              <header
                className="subs__cathead"
              >
                <div className="subs__cattitle">
                  <h2 lang={catalogNameLanguage(displayCategory)}>{displayCategory}</h2>
                  <div className="subs__catmeta">
                    {packageNames.length > 0 ? packageNames.join(' · ') : c('Ürün bazlı erişim', 'Product-based access')}
                  </div>
                </div>

                <div className="subs__catbadges">
                  {trial ? <Badge tone="warning">{c('Deneme', 'Trial')}</Badge> : <Badge tone="success">{c('Etkin', 'Active')}</Badge>}
                  <Link className="subs__catlink" to={`/katalog/${slugifyTr(category)}`}>
                    {c('Çalışma alanı', 'Workspace')} →
                  </Link>
                </div>
              </header>

              {wallet ? (
                <section className="subs__wallet" aria-label={`${displayCategory} kredi durumu`}>
                  <div>
                    <div className="subs__creditlabel">{c('Kalan kredi', 'Remaining credits')}</div>
                    <div className="subs__creditvalue">{formatNumber(wallet.balance)}</div>
                  </div>
                  <dl className="subs__creditside">
                    <div><dt>{c('Yüklenen', 'Granted')}</dt><dd>{formatNumber(wallet.granted)}</dd></div>
                    <div><dt>{c('Kullanılan', 'Used')}</dt><dd>{formatNumber(spent)}</dd></div>
                  </dl>
                  {wallet.granted > 0 ? (
                    <div className="subs__bar" role="progressbar" aria-valuenow={Math.round(usedPct)} aria-valuemin={0} aria-valuemax={100} aria-label={`${category} kullanılan kredi oranı`}>
                      <span className="subs__barfill" style={{ width: `${Math.min(100, usedPct)}%` }} />
                    </div>
                  ) : null}
                  <p className="small muted">{c('Bu kredi yalnız', 'These credits can only be used in the')} {displayCategory} {c('kataloğunda kullanılabilir.', 'catalog.')}</p>
                </section>
              ) : null}

              <ul className="subs__products">
                {products.map((p) => (
                  <li key={p.id} className="subs__product">
                    <Link to={`/kesfet/${p.slug}`} className="subs__productname">
                      {p.name}
                    </Link>
                    <span className="small muted">
                      {p.entitlementExpiresAt
                        ? language === 'tr' ? `${formatDate(p.entitlementExpiresAt)} tarihine kadar` : `Until ${formatDate(p.entitlementExpiresAt)}`
                        : c('Süresiz', 'No expiry')}
                    </span>
                  </li>
                ))}
              </ul>

              {expiries[0] ? (
                <footer className="subs__catfoot">
                  {c('Bu katalogda ilk yenileme', 'First renewal in this catalog')}: <strong>{formatDate(expiries[0])}</strong>
                </footer>
              ) : null}
            </Card>
          );
        })}
        </div>
      )}
    </div>
  );
}

function formatInvoiceMoney(minor: number, currency: string, language: 'tr' | 'en'): string {
  return new Intl.NumberFormat(language === 'tr' ? 'tr-TR' : 'en-US', { style: 'currency', currency }).format(minor / 100);
}

function offerStatus(value: string, language: 'tr' | 'en'): string {
  const tr = { draft: 'Hazırlanıyor', shared: 'İncelemeniz bekleniyor', reviewing: 'İnceleniyor', accepted: 'Kabul edildi', rejected: 'Reddedildi', payment_pending: 'Ödeme doğrulaması', payment_verified: 'Tamamlandı', expired: 'Süresi doldu', cancelled: 'İptal edildi' } as Record<string, string>;
  const en = { draft: 'Preparing', shared: 'Awaiting your review', reviewing: 'Under review', accepted: 'Accepted', rejected: 'Rejected', payment_pending: 'Payment verification', payment_verified: 'Completed', expired: 'Expired', cancelled: 'Cancelled' } as Record<string, string>;
  return (language === 'tr' ? tr : en)[value] ?? value;
}
